import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Plus, Package, Search, Trash2, Edit2, Image, Receipt, MapPin, Calendar, DollarSign, User, X, FileText, Lock } from 'lucide-react';

// Tack type options
const TACK_TYPES = [
  { value: 'saddle', label: 'Saddle' },
  { value: 'bridle', label: 'Bridle' },
  { value: 'cover', label: 'Cover' },
  { value: 'girth', label: 'Girth' },
  { value: 'saddle_pad', label: 'Saddle Pad' },
  { value: 'horse_boots', label: 'Horse Boots' },
  { value: 'halter_lead', label: 'Halter/Lead' },
  { value: 'transport', label: 'Transport' },
  { value: 'rider_apparel', label: 'Rider Apparel' },
  { value: 'miscellaneous', label: 'Miscellaneous' }
];

// GearForm defined OUTSIDE the main component to prevent re-render issues
const GearForm = ({ 
  data, 
  onChange, 
  onSubmit, 
  horses,
  riders,
  isSubmitting, 
  selectedReceiptFile, 
  setSelectedReceiptFile, 
  selectedPhotoFile, 
  setSelectedPhotoFile,
  receiptInputRef,
  photoInputRef,
  onDeletePhoto,
  onDeleteReceipt,
  isEdit = false 
}) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label htmlFor={isEdit ? "edit_item_name" : "item_name"}>Item Name *</Label>
      <Input 
        id={isEdit ? "edit_item_name" : "item_name"}
        value={data.item_name} 
        onChange={(e) => onChange({ ...data, item_name: e.target.value })} 
        placeholder="e.g., Leather Saddle, Bridle, Boots..."
        required 
        data-testid={isEdit ? "edit-gear-name-input" : "gear-name-input"}
      />
    </div>

    {/* Tack Type Dropdown */}
    <div className="space-y-2">
      <Label>Tack Type</Label>
      <Select value={data.tack_type || ''} onValueChange={(v) => onChange({ ...data, tack_type: v })}>
        <SelectTrigger data-testid={isEdit ? "edit-gear-type-select" : "gear-type-select"}>
          <SelectValue placeholder="Select type" />
        </SelectTrigger>
        <SelectContent>
          {TACK_TYPES.map(type => (
            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Size Field */}
    <div className="space-y-2">
      <Label>Size</Label>
      <Input 
        value={data.size || ''} 
        onChange={(e) => onChange({ ...data, size: e.target.value })} 
        placeholder="e.g., 17 inch, Medium, 48cm..."
        data-testid={isEdit ? "edit-gear-size-input" : "gear-size-input"}
      />
    </div>

    {/* Serial Number - Only for Saddles */}
    {data.tack_type === 'saddle' && (
      <div className="space-y-2">
        <Label>Serial Number</Label>
        <Input 
          value={data.serial_number || ''} 
          onChange={(e) => onChange({ ...data, serial_number: e.target.value })} 
          placeholder="Saddle serial number"
          data-testid={isEdit ? "edit-gear-serial-input" : "gear-serial-input"}
        />
      </div>
    )}

    <div className="space-y-2">
      <Label>Date Purchased</Label>
      <div className="relative">
        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input 
          type="date" 
          value={data.purchase_date || ''} 
          onChange={(e) => onChange({ ...data, purchase_date: e.target.value })} 
          className="pl-10"
          data-testid={isEdit ? "edit-gear-date-input" : "gear-date-input"}
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label>Purchase Location / Store</Label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input 
          value={data.purchase_location || ''} 
          onChange={(e) => onChange({ ...data, purchase_location: e.target.value })} 
          placeholder="e.g., Dover Saddlery, Amazon..."
          className="pl-10"
          data-testid={isEdit ? "edit-gear-location-input" : "gear-location-input"}
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label>Price</Label>
      <div className="relative">
        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input 
          type="number" 
          step="0.01" 
          min="0"
          value={data.cost || ''} 
          onChange={(e) => onChange({ ...data, cost: e.target.value })} 
          placeholder="0.00"
          className="pl-10"
          data-testid={isEdit ? "edit-gear-cost-input" : "gear-cost-input"}
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label>Receipt Photo</Label>
      {isEdit && data.receipt_url ? (
        <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
          <Receipt className="w-5 h-5 text-forest" />
          <span className="text-sm text-forest flex-1">Receipt uploaded</span>
          <Button 
            type="button" 
            variant="outline" 
            size="sm" 
            onClick={() => receiptInputRef.current?.click()}
            data-testid="change-receipt-btn"
          >
            Change
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="destructive" size="sm" data-testid="delete-receipt-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Receipt?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove the receipt image from this item.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDeleteReceipt} className="bg-destructive hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : (
        <div 
          className={`dropzone cursor-pointer ${selectedReceiptFile ? 'border-forest bg-forest/5' : ''}`}
          onClick={() => receiptInputRef.current?.click()}
        >
          <Receipt className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {selectedReceiptFile ? (
              <span className="text-forest font-medium">{selectedReceiptFile.name}</span>
            ) : (
              'Click to upload receipt image'
            )}
          </p>
        </div>
      )}
      <input
        ref={receiptInputRef}
        type="file"
        onChange={(e) => setSelectedReceiptFile(e.target.files?.[0] || null)}
        className="hidden"
        accept="image/*,.pdf"
      />
    </div>

    <div className="space-y-2">
      <Label>Item Photo</Label>
      {isEdit && data.photo_url ? (
        <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
          <Image className="w-5 h-5 text-forest" />
          <span className="text-sm text-forest flex-1">Photo uploaded</span>
          <Button 
            type="button" 
            variant="outline" 
            size="sm" 
            onClick={() => photoInputRef.current?.click()}
            data-testid="change-photo-btn"
          >
            Change
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="destructive" size="sm" data-testid="delete-photo-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Photo?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove the photo from this item.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDeletePhoto} className="bg-destructive hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : (
        <div 
          className={`dropzone cursor-pointer ${selectedPhotoFile ? 'border-forest bg-forest/5' : ''}`}
          onClick={() => photoInputRef.current?.click()}
        >
          <Image className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {selectedPhotoFile ? (
              <span className="text-forest font-medium">{selectedPhotoFile.name}</span>
            ) : (
              'Click to upload item photo'
            )}
          </p>
        </div>
      )}
      <input
        ref={photoInputRef}
        type="file"
        onChange={(e) => setSelectedPhotoFile(e.target.files?.[0] || null)}
        className="hidden"
        accept="image/*"
      />
    </div>

    {/* Allocation Type */}
    <div className="space-y-2">
      <Label>Assign To</Label>
      <Select 
        value={data.allocation_type || "general"} 
        onValueChange={(v) => onChange({ ...data, allocation_type: v, horse_id: '', rider_id: '' })}
      >
        <SelectTrigger data-testid={isEdit ? "edit-gear-allocation-type" : "gear-allocation-type"}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="general">General (Not assigned)</SelectItem>
          <SelectItem value="horse">A Horse</SelectItem>
          <SelectItem value="rider">A Rider</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {/* Horse Selection */}
    {data.allocation_type === 'horse' && (
      <div className="space-y-2">
        <Label>Select Horse</Label>
        <Select value={data.horse_id || ""} onValueChange={(v) => onChange({ ...data, horse_id: v })}>
          <SelectTrigger data-testid={isEdit ? "edit-gear-horse-select" : "gear-horse-select"}>
            <SelectValue placeholder="Select a horse" />
          </SelectTrigger>
          <SelectContent>
            {horses.filter(h => h.status === 'current' || h.horse_tab !== 'former').map(horse => (
              <SelectItem key={horse.horse_id} value={horse.horse_id}>
                {horse.stable_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )}

    {/* Rider Selection */}
    {data.allocation_type === 'rider' && (
      <div className="space-y-2">
        <Label>Select Rider</Label>
        <Select value={data.rider_id || ""} onValueChange={(v) => onChange({ ...data, rider_id: v })}>
          <SelectTrigger data-testid={isEdit ? "edit-gear-rider-select" : "gear-rider-select"}>
            <SelectValue placeholder="Select a rider" />
          </SelectTrigger>
          <SelectContent>
            {riders.map(rider => (
              <SelectItem key={rider.rider_id} value={rider.rider_id}>
                {rider.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )}

    <Button 
      type="submit" 
      className="w-full bg-forest hover:bg-forest-dark" 
      disabled={isSubmitting}
      data-testid={isEdit ? "submit-edit-gear-btn" : "submit-gear-btn"}
    >
      {isSubmitting ? 'Saving...' : isEdit ? 'Update Item' : 'Add Item'}
    </Button>
  </form>
);

export const GearPage = () => {
  const { token } = useAuth();
  const { canAddEdit, requireSubscription } = useSubscription();
  const [gear, setGear] = useState([]);
  const [horses, setHorses] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHorseId, setFilterHorseId] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedReceiptFile, setSelectedReceiptFile] = useState(null);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [editReceiptFile, setEditReceiptFile] = useState(null);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const receiptInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const editReceiptInputRef = useRef(null);
  const editPhotoInputRef = useRef(null);

  const emptyGear = {
    item_name: '',
    tack_type: '',
    size: '',
    serial_number: '',
    purchase_date: '',
    purchase_location: '',
    cost: '',
    horse_id: '',
    rider_id: '',
    allocation_type: 'general'
  };
  
  const [newGear, setNewGear] = useState(emptyGear);

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };
  
  // Get upload headers (no Content-Type for FormData)
  const getUploadHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = {};
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    const headers = getHeaders();
    try {
      const [gearRes, horsesRes, ridersRes] = await Promise.all([
        fetch(`${API}/gear`, { headers }),
        fetch(`${API}/horses`, { headers }),
        fetch(`${API}/rider-profiles`, { headers })
      ]);

      if (gearRes.ok) setGear(await gearRes.json());
      if (horsesRes.ok) setHorses(await horsesRes.json());
      if (ridersRes.ok) setRiders(await ridersRes.json());
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async (gearId) => {
    try {
      const response = await fetch(`${API}/gear/${gearId}/photo`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setGear(gear.map(g => g.gear_id === gearId ? { ...g, photo_url: null } : g));
        if (editingItem?.gear_id === gearId) {
          setEditingItem({ ...editingItem, photo_url: null });
        }
        toast.success('Photo deleted');
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleDeleteReceipt = async (gearId) => {
    try {
      const response = await fetch(`${API}/gear/${gearId}/receipt`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setGear(gear.map(g => g.gear_id === gearId ? { ...g, receipt_url: null } : g));
        if (editingItem?.gear_id === gearId) {
          setEditingItem({ ...editingItem, receipt_url: null });
        }
        toast.success('Receipt deleted');
      }
    } catch (error) {
      toast.error('Failed to delete receipt');
    }
  };

  const handleAddGear = async (e) => {
    e.preventDefault();
    if (!newGear.item_name) {
      toast.error('Item name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/gear`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          ...newGear,
          cost: newGear.cost ? parseFloat(newGear.cost) : null,
          horse_id: newGear.allocation_type === 'horse' ? newGear.horse_id : null,
          rider_id: newGear.allocation_type === 'rider' ? newGear.rider_id : null
        })
      });

      if (response.ok) {
        const item = await response.json();
        
        // Upload files if selected
        if (selectedReceiptFile) {
          const formData = new FormData();
          formData.append('file', selectedReceiptFile);
          formData.append('type', 'receipt');
          const uploadRes = await fetch(`${API}/upload/gear/${item.gear_id}`, {
            method: 'POST',
            headers: getUploadHeaders(),
            body: formData
          });
          if (uploadRes.ok) {
            const data = await uploadRes.json();
            item.receipt_url = data.file_url;
          }
        }

        if (selectedPhotoFile) {
          const formData = new FormData();
          formData.append('file', selectedPhotoFile);
          formData.append('type', 'photo');
          const uploadRes = await fetch(`${API}/upload/gear/${item.gear_id}`, {
            method: 'POST',
            headers: getUploadHeaders(),
            body: formData
          });
          if (uploadRes.ok) {
            const data = await uploadRes.json();
            item.photo_url = data.file_url;
          }
        }

        setGear([...gear, item]);
        setDialogOpen(false);
        setNewGear(emptyGear);
        setSelectedReceiptFile(null);
        setSelectedPhotoFile(null);
        toast.success('Item added to tack vault!');
      }
    } catch (error) {
      toast.error('Failed to add item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditGear = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/gear/${editingItem.gear_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          item_name: editingItem.item_name,
          tack_type: editingItem.tack_type || null,
          size: editingItem.size || null,
          serial_number: editingItem.serial_number || null,
          purchase_date: editingItem.purchase_date,
          purchase_location: editingItem.purchase_location,
          cost: editingItem.cost ? parseFloat(editingItem.cost) : null,
          horse_id: editingItem.allocation_type === 'horse' ? editingItem.horse_id : null,
          rider_id: editingItem.allocation_type === 'rider' ? editingItem.rider_id : null,
          allocation_type: editingItem.allocation_type
        })
      });

      if (response.ok) {
        let updated = await response.json();

        // Upload new files if selected
        if (editReceiptFile) {
          const formData = new FormData();
          formData.append('file', editReceiptFile);
          formData.append('type', 'receipt');
          const uploadRes = await fetch(`${API}/upload/gear/${editingItem.gear_id}`, {
            method: 'POST',
            headers: getUploadHeaders(),
            body: formData
          });
          if (uploadRes.ok) {
            const data = await uploadRes.json();
            updated.receipt_url = data.file_url;
          }
        }

        if (editPhotoFile) {
          const formData = new FormData();
          formData.append('file', editPhotoFile);
          formData.append('type', 'photo');
          const uploadRes = await fetch(`${API}/upload/gear/${editingItem.gear_id}`, {
            method: 'POST',
            headers: getUploadHeaders(),
            body: formData
          });
          if (uploadRes.ok) {
            const data = await uploadRes.json();
            updated.photo_url = data.file_url;
          }
        }

        setGear(gear.map(g => g.gear_id === updated.gear_id ? updated : g));
        setEditDialogOpen(false);
        setEditingItem(null);
        setEditReceiptFile(null);
        setEditPhotoFile(null);
        toast.success('Item updated!');
      }
    } catch (error) {
      toast.error('Failed to update item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGear = async (gearId) => {
    try {
      const response = await fetch(`${API}/gear/${gearId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        setGear(gear.filter(g => g.gear_id !== gearId));
        toast.success('Item deleted');
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const openEditDialog = (item) => {
    setEditingItem({
      ...item,
      allocation_type: item.allocation_type || (item.horse_id ? 'horse' : item.rider_id ? 'rider' : 'general')
    });
    setEditDialogOpen(true);
  };

  const openViewDialog = (item) => {
    setViewingItem(item);
    setViewDialogOpen(true);
  };

  const getHorseName = (horseId) => {
    if (!horseId) return 'General';
    const horse = horses.find(h => h.horse_id === horseId);
    return horse?.stable_name || 'Unknown';
  };

  const getRiderName = (riderId) => {
    if (!riderId) return null;
    const rider = riders.find(r => r.rider_id === riderId);
    return rider?.name || 'Unknown';
  };

  const getAssignedTo = (item) => {
    if (item.allocation_type === 'horse' && item.horse_id) {
      return `🐴 ${getHorseName(item.horse_id)}`;
    }
    if (item.allocation_type === 'rider' && item.rider_id) {
      return `👤 ${getRiderName(item.rider_id)}`;
    }
    return 'General';
  };

  const filteredGear = gear.filter(item => {
    const matchesSearch = !searchTerm || 
      item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.purchase_location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Enhanced filter logic
    let matchesFilter = true;
    if (filterHorseId === 'all') {
      matchesFilter = true;
    } else if (filterHorseId === 'general') {
      matchesFilter = item.allocation_type === 'general' || (!item.horse_id && !item.rider_id);
    } else if (filterHorseId === 'horses') {
      matchesFilter = item.allocation_type === 'horse' && item.horse_id;
    } else if (filterHorseId === 'riders') {
      matchesFilter = item.allocation_type === 'rider' && item.rider_id;
    } else if (filterHorseId.startsWith('type_')) {
      const tackType = filterHorseId.replace('type_', '');
      matchesFilter = item.tack_type === tackType;
    } else if (filterHorseId.startsWith('horse_')) {
      matchesFilter = item.horse_id === filterHorseId.replace('horse_', '');
    } else if (filterHorseId.startsWith('rider_')) {
      matchesFilter = item.rider_id === filterHorseId.replace('rider_', '');
    }

    return matchesSearch && matchesFilter;
  });

  const totalValue = gear.reduce((sum, item) => sum + (item.cost || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="h-10 w-32 skeleton rounded-lg" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-48 skeleton rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="gear-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Tack Vault</h1>
            <p className="text-muted-foreground mt-1">
              {gear.length} items • ${totalValue.toFixed(2)} total value
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-forest hover:bg-forest-dark gap-2 disabled:opacity-50" 
                data-testid="add-gear-btn"
                onClick={(e) => {
                  if (!canAddEdit()) {
                    e.preventDefault();
                    requireSubscription();
                  }
                }}
                disabled={!canAddEdit()}
              >
                {!canAddEdit() && <Lock className="w-4 h-4" />}
                <Plus className="w-4 h-4" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif">Add to Tack Vault</DialogTitle>
              </DialogHeader>
              <GearForm
                data={newGear}
                onChange={setNewGear}
                onSubmit={handleAddGear}
                horses={horses}
                riders={riders}
                isSubmitting={isSubmitting}
                selectedReceiptFile={selectedReceiptFile}
                setSelectedReceiptFile={setSelectedReceiptFile}
                selectedPhotoFile={selectedPhotoFile}
                setSelectedPhotoFile={setSelectedPhotoFile}
                receiptInputRef={receiptInputRef}
                photoInputRef={photoInputRef}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-gear-input"
            />
          </div>
          <Select value={filterHorseId} onValueChange={setFilterHorseId}>
            <SelectTrigger className="w-full sm:w-56 bg-white" data-testid="filter-gear-select">
              <SelectValue placeholder="Filter by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="general">General (Unassigned)</SelectItem>
              <SelectItem value="horses">All Horses</SelectItem>
              <SelectItem value="riders">All Riders</SelectItem>
              {horses.filter(h => h.status === 'current' || h.horse_tab !== 'former').length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">By Horse</div>
                  {horses.filter(h => h.status === 'current' || h.horse_tab !== 'former').map(horse => (
                    <SelectItem key={`horse_${horse.horse_id}`} value={`horse_${horse.horse_id}`}>
                      🐴 {horse.stable_name}
                    </SelectItem>
                  ))}
                </>
              )}
              {riders.length > 0 && (
                <>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">By Rider</div>
                  {riders.map(rider => (
                    <SelectItem key={`rider_${rider.rider_id}`} value={`rider_${rider.rider_id}`}>
                      👤 {rider.name}
                    </SelectItem>
                  ))}
                </>
              )}
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">By Tack Type</div>
              {TACK_TYPES.map(type => (
                <SelectItem key={`type_${type.value}`} value={`type_${type.value}`}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Link to="/reports?type=tack">
            <Button variant="outline" className="gap-2" data-testid="generate-tack-report-btn">
              <FileText className="w-4 h-4" />
              Generate Report
            </Button>
          </Link>
        </div>

        {/* Grid */}
        {gear.length === 0 ? (
          <Card className="border-0 shadow-card bg-white">
            <CardContent className="py-16 text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
              <h3 className="text-lg font-semibold mb-2">No Tack Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Add your saddles, bridles, boots, and other tack to keep track of your investments.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredGear.map((item, i) => (
              <Card 
                key={item.gear_id} 
                className="border-0 shadow-card bg-white card-hover overflow-hidden animate-slideUp cursor-pointer" 
                style={{ animationDelay: `${i * 0.05}s` }}
                onClick={() => openViewDialog(item)}
                data-testid={`gear-card-${item.gear_id}`}
              >
                {item.photo_url ? (
                  <div className="aspect-square bg-muted max-h-24">
                    <img 
                      src={item.photo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.photo_url}` : item.photo_url}
                      alt={item.item_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-square bg-gradient-to-br from-saddle/10 to-forest/10 flex items-center justify-center max-h-24">
                    <Package className="w-8 h-8 text-saddle/30" />
                  </div>
                )}
                
                <CardContent className="p-3">
                  <h3 className="font-semibold text-sm truncate mb-1">{item.item_name}</h3>
                  
                  <div className="space-y-0.5 text-xs">
                    {item.cost && <p className="font-semibold text-sm text-saddle">${item.cost.toFixed(2)}</p>}
                    <p className="text-muted-foreground truncate">
                      {getAssignedTo(item)}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7"
                      onClick={(e) => { e.stopPropagation(); openEditDialog(item); }}
                      data-testid={`edit-gear-${item.gear_id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7 text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDeleteGear(item.gear_id); }}
                      data-testid={`delete-gear-${item.gear_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingItem(null); setEditReceiptFile(null); setEditPhotoFile(null); } }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Edit Item</DialogTitle>
            </DialogHeader>
            {editingItem && (
              <GearForm
                data={editingItem}
                onChange={setEditingItem}
                onSubmit={handleEditGear}
                horses={horses}
                riders={riders}
                isSubmitting={isSubmitting}
                selectedReceiptFile={editReceiptFile}
                setSelectedReceiptFile={setEditReceiptFile}
                selectedPhotoFile={editPhotoFile}
                setSelectedPhotoFile={setEditPhotoFile}
                receiptInputRef={editReceiptInputRef}
                photoInputRef={editPhotoInputRef}
                onDeletePhoto={() => handleDeletePhoto(editingItem.gear_id)}
                onDeleteReceipt={() => handleDeleteReceipt(editingItem.gear_id)}
                isEdit={true}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={(open) => { setViewDialogOpen(open); if (!open) setViewingItem(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif">{viewingItem?.item_name}</DialogTitle>
            </DialogHeader>
            {viewingItem && (
              <div className="space-y-4 mt-2">
                {viewingItem.photo_url && (
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                    <img 
                      src={viewingItem.photo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${viewingItem.photo_url}` : viewingItem.photo_url}
                      alt={viewingItem.item_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="space-y-3">
                  {viewingItem.cost && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <span className="font-semibold text-lg text-saddle">${viewingItem.cost.toFixed(2)}</span>
                    </div>
                  )}

                  {viewingItem.tack_type && (
                    <div className="flex items-center gap-2 text-sm">
                      <Package className="w-4 h-4 text-muted-foreground" />
                      <span>Type: {TACK_TYPES.find(t => t.value === viewingItem.tack_type)?.label || viewingItem.tack_type}</span>
                    </div>
                  )}

                  {viewingItem.size && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-4 h-4 text-muted-foreground text-center">📏</span>
                      <span>Size: {viewingItem.size}</span>
                    </div>
                  )}

                  {viewingItem.serial_number && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-4 h-4 text-muted-foreground text-center">#</span>
                      <span>Serial: {viewingItem.serial_number}</span>
                    </div>
                  )}
                  
                  {viewingItem.purchase_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span>Purchased {format(parseISO(viewingItem.purchase_date), 'MMM d, yyyy')}</span>
                    </div>
                  )}
                  
                  {viewingItem.purchase_location && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span>{viewingItem.purchase_location}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm">
                    {viewingItem.allocation_type === 'horse' ? (
                      <>
                        <span className="text-lg">🐴</span>
                        <span>Assigned to {getHorseName(viewingItem.horse_id)}</span>
                      </>
                    ) : viewingItem.allocation_type === 'rider' ? (
                      <>
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span>Assigned to {getRiderName(viewingItem.rider_id)}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">General (not assigned)</span>
                    )}
                  </div>
                  
                  {viewingItem.receipt_url && (
                    <a 
                      href={viewingItem.receipt_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${viewingItem.receipt_url}` : viewingItem.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-forest hover:underline"
                    >
                      <Receipt className="w-4 h-4" /> View Receipt
                    </a>
                  )}
                </div>
                
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setViewDialogOpen(false); openEditDialog(viewingItem); }}
                  >
                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    className="text-destructive"
                    onClick={() => { handleDeleteGear(viewingItem.gear_id); setViewDialogOpen(false); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
